import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScreenKey } from '@/types/domain';

type TabId = 'events' | 'map' | 'home' | 'follow' | 'saved';

interface TabBarProps {
  active: ScreenKey;
  onChange: (screen: TabId) => void;
  /** Increments on each save — floats a "+1" off the Saved tab. */
  savePulse?: number;
}

interface Tab {
  id: TabId;
  labelKey: string;
  icon: 'home' | 'map' | 'bell' | 'bookmark' | 'cal';
}

const TABS: Tab[] = [
  { id: 'events', labelKey: 'common:nav.events', icon: 'cal' },
  { id: 'map', labelKey: 'common:nav.map', icon: 'map' },
  { id: 'home', labelKey: 'common:nav.today', icon: 'home' },
  { id: 'follow', labelKey: 'common:nav.follow', icon: 'bell' },
  { id: 'saved', labelKey: 'common:nav.saved', icon: 'bookmark' },
];

function Icon({ id, active }: { id: Tab['icon']; active: boolean }) {
  const stroke = active ? '#0a0a0a' : '#9a9590';
  if (id === 'home') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        <path d="M3 11l9-8 9 8v10a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2V11z" />
      </svg>
    );
  }
  if (id === 'cal') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
    );
  }
  if (id === 'map') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        <path d="M9 4l-6 2v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
      </svg>
    );
  }
  if (id === 'bell') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10 21a2 2 0 004 0" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
      <path d="M5 4h14v18l-7-4-7 4V4z" />
    </svg>
  );
}

// "country", "match", "pick-venue", and "schedule" are sub-routes navigated
// to from the home tab — highlight the home tab while the user is there.
function tabFor(screen: ScreenKey): TabId {
  if (
    screen === 'country' ||
    screen === 'match' ||
    screen === 'pick-venue' ||
    screen === 'schedule'
  ) {
    return 'home';
  }
  return screen as TabId;
}

// --- Saved-tab "+1" delight (revertible block) ---
const PLUS_ONE = '+1';
const PLUS_ONE_KEYFRAME = `
@keyframes nynjwc-plus-one {
  0% { opacity: 0; transform: translateX(-50%) translateY(4px) scale(0.7); }
  20% { opacity: 1; transform: translateX(-50%) translateY(-2px) scale(1.1); }
  45% { opacity: 1; transform: translateX(-50%) translateY(-6px) scale(1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-20px) scale(1); }
}`;
const plusOneStyle: CSSProperties = {
  position: 'absolute',
  top: -10,
  left: '50%',
  fontFamily: '-apple-system, system-ui',
  fontSize: 12,
  fontWeight: 800,
  color: '#0a7f0a',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  animation: 'nynjwc-plus-one 700ms cubic-bezier(.22,1,.36,1) forwards',
};
// --- end Saved-tab "+1" block ---

export function TabBar({ active, onChange, savePulse = 0 }: TabBarProps) {
  const { t } = useTranslation();
  const highlighted = tabFor(active);
  // "+1" delight: retrigger the float-up on the Saved tab when savePulse ticks
  // up (skips the initial mount value, so boot bookmark-load doesn't fire it).
  const [plusKey, setPlusKey] = useState(0);
  const prevPulse = useRef(savePulse);
  useEffect(() => {
    if (savePulse !== prevPulse.current) {
      prevPulse.current = savePulse;
      setPlusKey((k) => k + 1);
    }
  }, [savePulse]);
  return (
    <nav
      aria-label={t('common:nav.primary')}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '0.5px solid rgba(0,0,0,0.08)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        paddingTop: 8,
      }}
    >
      <style>{PLUS_ONE_KEYFRAME}</style>
      <div
        style={{
          maxWidth: 560,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-around',
        }}
      >
        {TABS.map((tab) => {
          const isActive = highlighted === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '4px 8px',
                flex: 1,
              }}
            >
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <Icon id={tab.icon} active={isActive} />
                {tab.id === 'saved' && plusKey > 0 ? (
                  <span key={plusKey} aria-hidden="true" style={plusOneStyle}>
                    {PLUS_ONE}
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  fontFamily: '-apple-system, system-ui',
                  fontSize: 10,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#0a0a0a' : '#9a9590',
                  letterSpacing: 0.2,
                }}
              >
                {t(tab.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
